import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { ShieldCheck } from "lucide-react";

const YEAR = new Date().getFullYear();

function Section({ title, children }) {
  return (
    <div>
      <h4 className="font-serif text-lg text-[#2B2622] mb-1.5">{title}</h4>
      <div className="text-sm text-[#5C5651] leading-relaxed space-y-2">{children}</div>
    </div>
  );
}

export default function Footer() {
  const [open, setOpen] = useState(false);
  return (
    <footer data-testid="app-footer" className="border-t border-[#EBE5E0] bg-white mt-16">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-[11px] uppercase tracking-[0.18em] text-[#9b938b]">
          © {YEAR} WorkRoom · Cueillir Collection
        </p>
        <button data-testid="open-privacy-btn" onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#5C5651] hover:text-[#A68A6B] transition-colors">
          <ShieldCheck size={14} strokeWidth={1.5} /> Information &amp; Privacy
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border-[#EBE5E0] bg-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="privacy-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#2B2622]">Information &amp; Privacy</DialogTitle>
            <DialogDescription className="text-xs uppercase tracking-wider text-[#A68A6B]">
              How WorkRoom collects, uses and protects your data
            </DialogDescription>
          </DialogHeader>

          <div className="mt-3 space-y-5">
            <p className="text-sm text-[#5C5651] leading-relaxed">
              WorkRoom is a project workspace for interior designers and their clients. We collect only
              the information needed to run your projects and keep your account secure. This summary
              explains what we store and how it is handled.
            </p>

            <Section title="Information we collect">
              <ul className="list-disc pl-5 space-y-1">
                <li><span className="text-[#2B2622] font-medium">Account details</span> — your name, email address and role (designer, team member or client). Passwords are stored only as a securely hashed value, never in plain text.</li>
                <li><span className="text-[#2B2622] font-medium">Project content</span> — project names, budgets, purchase orders, invoices, proposals, approvals, mood boards, design schemes, notes, calendar tasks, checklists, documents and photos you add.</li>
                <li><span className="text-[#2B2622] font-medium">Uploaded files &amp; images</span> — stored in secure object storage and served only to people with access to the related project.</li>
                <li><span className="text-[#2B2622] font-medium">Assistant activity</span> — questions and responses from the in-app AI design assistant, saved to your project so you can revisit them.</li>
              </ul>
            </Section>

            <Section title="Payments">
              <p>
                Subscription billing is processed by <span className="text-[#2B2622] font-medium">Square</span>.
                Card details are entered directly with Square and are never stored on WorkRoom's servers — we
                only keep a Square customer reference and your subscription status (trial, active, past due or canceled).
              </p>
            </Section>

            <Section title="Third-party services">
              <ul className="list-disc pl-5 space-y-1">
                <li><span className="text-[#2B2622] font-medium">Square</span> — subscription and payment processing.</li>
                <li><span className="text-[#2B2622] font-medium">AI provider</span> — assistant prompts are sent to a third-party language-model provider to generate responses. Do not include sensitive personal data in assistant chats.</li>
                <li><span className="text-[#2B2622] font-medium">Email delivery</span> — transactional emails (invites, notes, invoices, proposals) are sent through an email delivery provider.</li>
              </ul>
            </Section>

            <Section title="How your data is used">
              <p>
                Your information is used solely to operate WorkRoom — to display your projects, share the
                right content with the right people, process billing and send project notifications. We do
                not sell your data or use it for third-party advertising.
              </p>
            </Section>

            <Section title="Access &amp; sharing controls">
              <p>
                Designers control exactly what each client can view and edit on a per-project basis. Clients
                only ever see the projects they are invited to and the sections a designer has shared with them.
              </p>
            </Section>

            <Section title="Who can see your project details">
              <p>
                Only the individuals associated with a project — the designer, their invited team members and
                the invited client — can view that project and its details. Project information is shown solely
                to these authorized people according to the access a designer has granted.
              </p>
              <p>
                As the service provider, we do not view, monitor or use the contents of your projects. Your
                project details are yours; we only maintain the platform that stores and displays them to the
                people you've authorized.
              </p>
            </Section>

            <Section title="Data storage &amp; security">
              <p>
                Data is stored in a managed database with files in secure object storage. Access is protected
                by authenticated, token-based sessions. We take reasonable measures to protect your data,
                though no online service can guarantee absolute security.
              </p>
            </Section>

            <Section title="Retention &amp; your choices">
              <p>
                Project data is retained while your account is active. Deleting a project permanently removes
                its content, including budgets, approvals, mood boards, notes, documents and files. To request
                access to, correction of, or deletion of your account data, contact us using the details below.
              </p>
            </Section>

            <Section title="Contact">
              <p>
                Questions about your data or this notice? Email{" "}
                <a href="mailto:info@cueillircollection.com" className="text-[#A68A6B] hover:underline">
                  info@cueillircollection.com
                </a>.
              </p>
            </Section>

            <p className="text-[11px] text-[#9b938b] pt-2 border-t border-[#F2EFEB]">
              This notice may be updated from time to time. Last updated {new Date().toLocaleDateString([], { month: "long", year: "numeric" })}.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </footer>
  );
}
